import type { ApiErrorBody } from '@shop/shared';
import { en, lt } from './dictionary';
import { getRequestLocale } from './locale-state';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fields?: Record<string, string>,
    public details?: unknown,
  ) {
    super(message);
  }
}

/** Storefront endpoints answer in the page's language. */
function withLang(path: string) {
  const locale = getRequestLocale();
  if (locale === 'en' || !path.startsWith('/api/store/')) return path;
  return `${path}${path.includes('?') ? '&' : '?'}lang=${locale}`;
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  const dict = getRequestLocale() === 'lt' ? lt : en;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${withLang(path)}`, {
      credentials: 'include',
      ...rest,
      headers: {
        Accept: 'application/json',
        ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: json !== undefined ? JSON.stringify(json) : rest.body,
    });
  } catch {
    throw new ApiError(0, 'network_error', dict.common.networkError);
  }
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    const err = (body as ApiErrorBody | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? dict.common.somethingWrong, err?.fields, (err as { details?: unknown } | undefined)?.details);
  }
  return body as T;
}

function safeJson(text: string) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Uploaded images are stored as /uploads/... paths on the API origin. */
export function assetUrl(url: string | null | undefined): string {
  if (!url) return '';
  if (url.startsWith('/uploads/') && API_BASE) return `${API_BASE}${url}`;
  return url;
}

export function qs(params: Record<string, string | number | boolean | undefined | null>) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '' && v !== false) sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}
